import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronRight, ExternalLink, RefreshCw, Search, Ticket, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type EventPurchaseRow,
  type EventPurchaseStatusFilter,
  fetchEventPurchases,
  fetchEventSummary,
  isGuestBuyer,
  purchaseAttendeesLine,
} from '@/api/admin-event-purchases';
import { Avatar, formatDate, StatusPill } from '@/components/admin/billing/BillingBits';
import { formatCents } from '@/lib/money';

export const Route = createFileRoute('/admin/event-purchases')({
  component: EventPurchasesPage,
});

const FILTERS: { key: EventPurchaseStatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'refunded', label: 'Refunded' },
  { key: 'sold_out', label: 'Sold out' },
];

function EventPurchasesPage() {
  const [filter, setFilter] = useState<EventPurchaseStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin-event-purchases'],
    queryFn: fetchEventPurchases,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const rows = query.data?.rows ?? [];
  const counts = query.data?.counts;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!q) return true;
      return (
        r.eventTitle.toLowerCase().includes(q) ||
        (r.buyer.name?.toLowerCase().includes(q) ?? false) ||
        (r.buyer.email?.toLowerCase().includes(q) ?? false) ||
        (r.organizationName?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [rows, filter, search]);

  // Escape closes the detail panel.
  useEffect(() => {
    if (!selectedEventId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEventId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEventId]);

  const countFor = (f: EventPurchaseStatusFilter): number | undefined => {
    if (!counts) return undefined;
    return counts[f];
  };

  return (
    <div className="flex h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl space-y-5 p-6">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">Event Purchases</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Every paid-event ticket sold. Click a row for the event's sales and attendees.
              </p>
            </div>
            <button
              onClick={() => query.refetch()}
              className="rounded-md border border-border bg-background p-2 text-muted-foreground transition-colors hover:border-borderStrong hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              title="Refresh"
              aria-label="Refresh purchases"
            >
              <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Filters + search */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => {
                const c = countFor(f.key);
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    aria-pressed={active}
                    className={`rounded-full border px-3 py-1 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-muted-foreground hover:border-borderStrong hover:text-foreground'
                    }`}
                  >
                    {f.label}
                    {c != null && (
                      <span className={`ml-1.5 tabular-nums ${active ? 'opacity-80' : 'opacity-60'}`}>
                        {c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="relative ml-auto">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search event, buyer, email…"
                aria-label="Search purchases"
                className="w-64 rounded-md border border-border bg-background py-1.5 pl-8 pr-8 text-sm text-foreground outline-none transition-colors focus:border-primary"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  title="Clear search"
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* States */}
          {query.isLoading && (
            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                  <Skeleton className="h-4 w-44" />
                  <Skeleton className="ml-auto h-4 w-16" />
                </div>
              ))}
            </div>
          )}

          {query.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {query.error instanceof Error ? query.error.message : 'Failed to load purchases.'}
              </AlertDescription>
            </Alert>
          )}

          {query.data && visible.length === 0 && (
            <div className="rounded-xl border border-border p-12 text-center">
              <Ticket className="mx-auto mb-3 h-7 w-7 text-muted-foreground" />
              <p className="text-lg font-semibold text-foreground">No ticket sales yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {rows.length === 0
                  ? 'Paid-event tickets will appear here once someone buys one.'
                  : 'No purchases match these filters.'}
              </p>
            </div>
          )}

          {/* Purchase ledger */}
          {query.data && visible.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-card text-left">
                    <Th>Event</Th>
                    <Th>Buyer</Th>
                    <Th className="text-right">Amount</Th>
                    <Th>Status</Th>
                    <Th className="hidden md:table-cell">Purchased</Th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const isSel = selectedEventId === r.eventId;
                    const guest = isGuestBuyer(r);
                    // Guests have no organisation; their email is the useful
                    // second line when the name already occupies the first.
                    const secondary = r.organizationName
                      ?? (guest && r.buyer.name ? r.buyer.email : null);
                    return (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedEventId(r.eventId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedEventId(r.eventId);
                          }
                        }}
                        tabIndex={0}
                        aria-label={`${r.eventTitle}, ${r.buyer.name ?? r.buyer.email ?? 'buyer'}, ${r.status}. Open event details`}
                        className={`group cursor-pointer border-b border-border outline-none transition-colors last:border-0 hover:bg-cardHover focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring ${
                          isSel ? 'bg-primary/[0.07]' : ''
                        }`}
                      >
                        <td className="py-2.5 pl-4 pr-3">
                          <p className="truncate font-medium text-foreground">{r.eventTitle}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {formatDate(r.eventStartAt)}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <Avatar name={r.buyer.name ?? r.buyer.email} src={r.buyer.avatarUrl} size={28} />
                            <div className="min-w-0">
                              <p className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-foreground">
                                <span className="truncate">
                                  {r.buyer.name ?? r.buyer.email ?? 'Unknown'}
                                </span>
                                {guest && <GuestPill />}
                              </p>
                              {secondary && (
                                <p className="truncate text-xs text-muted-foreground">{secondary}</p>
                              )}
                              {purchaseAttendeesLine(r) && (
                                <p className="truncate text-xs text-muted-foreground">
                                  {purchaseAttendeesLine(r)}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-foreground">
                          {formatCents(r.amountCents, r.currency)}
                          {(r.quantity ?? 1) > 1 && (
                            <p className="text-xs font-normal text-muted-foreground">
                              {r.quantity} tickets
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <StatusPill status={r.status} />
                        </td>
                        <td className="hidden px-3 py-2.5 text-muted-foreground md:table-cell">
                          {formatDate(r.createdAt)}
                        </td>
                        <td className="pr-4 text-right">
                          <ChevronRight
                            className={`inline-block h-4 w-4 align-middle transition-opacity ${
                              isSel
                                ? 'text-foreground opacity-100'
                                : 'text-muted-foreground opacity-30 group-hover:opacity-70'
                            }`}
                            aria-hidden
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail — inline split column on lg+, slide-over with scrim below */}
      {selectedEventId && (
        <>
          <div
            className="fixed inset-0 z-20 bg-black/40 lg:hidden"
            onClick={() => setSelectedEventId(null)}
            aria-hidden
          />
          <aside
            aria-label="Event sales detail"
            className="fixed inset-y-0 right-0 z-30 w-full max-w-md shrink-0 overflow-y-auto border-l border-border bg-card animate-in fade-in duration-200 ease-out motion-reduce:animate-none max-lg:slide-in-from-right-4 lg:static lg:z-auto lg:w-[26rem] lg:max-w-none"
          >
            <EventDetailPanel eventId={selectedEventId} onClose={() => setSelectedEventId(null)} />
          </aside>
        </>
      )}
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:pl-4 ${className}`}
    >
      {children}
    </th>
  );
}

function EventDetailPanel({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['admin-event-summary', eventId],
    queryFn: () => fetchEventSummary(eventId),
    staleTime: 60 * 1000,
  });
  const summary = query.data;

  return (
    <div className="space-y-5 p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Event sales
          </span>
          <h2 className="mt-1 truncate text-xl font-bold tracking-tight text-foreground">
            {summary?.eventTitle ?? 'Loading…'}
          </h2>
          {summary && (
            <p className="mt-0.5 text-xs text-muted-foreground">{formatDate(summary.eventStartAt)}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          title="Close"
          aria-label="Close details"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {query.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      )}

      {summary && (
        <>
          {/* Revenue hero */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Gross revenue
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                {formatCents(summary.grossCents, summary.currency)}
              </p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Tickets sold
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                {summary.ticketCount}
                {summary.refundedCount > 0 && (
                  <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                    · {summary.refundedCount} refunded
                  </span>
                )}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Purchasers · {summary.purchasers.length}
            </p>
            <div className="-my-1.5 divide-y divide-border">
              {summary.purchasers.map((p) => <PurchaserRow key={p.id} purchase={p} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Neutral marker for a buyer with no Alfie account (public-link checkout). */
function GuestPill() {
  return (
    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
      Guest
    </span>
  );
}

function PurchaserRow({ purchase }: { purchase: EventPurchaseRow }) {
  const refunded = purchase.status === 'refunded';
  const guest = isGuestBuyer(purchase);
  return (
    <div className={`flex items-center gap-2.5 py-2 ${refunded ? 'opacity-60' : ''}`}>
      <Avatar name={purchase.buyer.name ?? purchase.buyer.email} src={purchase.buyer.avatarUrl} size={28} />
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium leading-tight text-foreground">
          <span className="truncate">
            {purchase.buyer.name ?? purchase.buyer.email ?? 'Unknown buyer'}
          </span>
          {guest && <GuestPill />}
        </p>
        <p className="truncate text-xs leading-tight text-muted-foreground">
          {formatDate(purchase.createdAt)}
          {(purchase.quantity ?? 1) > 1 && ` · ${purchase.quantity} tickets`}
        </p>
        {purchaseAttendeesLine(purchase) && (
          <p className="truncate text-xs leading-tight text-muted-foreground">
            {purchaseAttendeesLine(purchase)}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 pl-2">
        <span className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-foreground">
          {formatCents(purchase.amountCents, purchase.currency)}
        </span>
        {refunded && <StatusPill status="refunded" />}
        {purchase.stripePaymentIntentId && (
          <a
            href={`https://dashboard.stripe.com/test/payments/${purchase.stripePaymentIntentId}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="rounded p-1 text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            title="Open payment in Stripe"
            aria-label="Open payment in Stripe"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}
